// 購入予定リスト画面
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  amazonSearchUrl,
  koshoSearchUrl,
} from '../ui.js';
import {
  getAllWishlist,
  removeFromWishlist,
  addToWishlist,
  wishlistId,
  addLocalBook,
} from '../cache.js';
import { extractBookFromUrl, extractBookFromImage } from '../api.js';

const GENRE_OPTIONS = [
  '哲学', '思想', '宗教', 'ビジネス', '歴史', '芸術', '写真', '建築',
  'デザイン', '文学', '小説', 'エッセイ', '科学', '心理学', '社会学',
];

// 画像を最大辺 maxSide 以下に縮小し JPEG Blob で返す。
// iPhone の HEIC が直接送れない問題も、Canvas 経由で JPEG に変換することで解消する。
async function downscaleImage(file, maxSide) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  let { width, height } = img;
  const scale = Math.min(1, maxSide / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
}

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

export async function showWishlistScreen() {
  const items = await getAllWishlist();
  renderList(items);
}

function renderList(items) {
  const screenContent = items.length
    ? `${renderAddButton()}
       <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 24px;">
         ${items.length} 冊が登録されています。タップで詳細を見たり、購入リンクから探せます。
       </p>
       ${items.map((it) => renderItem(it)).join('')}`
    : `${renderAddButton()}
       <p class="empty">まだ何も登録されていません。<br/>「おすすめの本」や「キーワードから探す」で気になる本を見つけたら、各書籍の「購入予定リストに加える」ボタンから登録できます。<br/><br/>または上の <strong>＋ 手動で追加</strong> から自分で書き込むこともできます。</p>`;

  const screen = showScreen(
    'wishlist',
    `${screenHead('購入予定リスト')}${screenContent}`
  );
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  bindAddButton(screen);
  bindItemHandlers(screen);
}

function renderAddButton() {
  return `
    <button id="btn-wishlist-add" class="btn btn--ink btn--block" type="button" style="margin-bottom:24px;">
      <svg class="icon"><use href="#i-plus"/></svg>
      <span>手動で追加</span>
    </button>
  `;
}

function bindAddButton(screen) {
  const btn = screen.querySelector('#btn-wishlist-add');
  if (!btn) return;
  btn.addEventListener('click', () => renderAddForm());
}

function renderAddForm() {
  const genreChips = GENRE_OPTIONS.map(
    (g) => `<button type="button" class="genre-tab" data-genre-chip="${escapeHtml(g)}">${escapeHtml(g)}</button>`
  ).join('');

  const html = `
    ${screenHead('手動で追加')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      URL を貼り付けると AI が書名・著者・概要を自動抽出します。<br/>
      手動入力も併用可能です。
    </p>
    <div class="form-group" style="padding:14px 16px;border:1px solid var(--line);background:var(--paper);">
      <label class="form-label">書籍ページの URL から自動取得</label>
      <input class="form-input" id="f-url" type="url" placeholder="https://www.amazon.co.jp/..." autocomplete="off" />
      <button class="btn btn--ink btn--small btn--block" id="btn-extract" type="button" style="margin-top:8px;">
        <svg class="icon"><use href="#i-sparkles"/></svg>
        <span>AI で読み取る</span>
      </button>
      <p style="margin:8px 0 0;font-size:10.5px;color:var(--mute);line-height:1.6;">
        Amazon・日本の古本屋・出版社サイト・書評記事など、どのサイトでも対応。読み取った内容はフォームに自動入力され、編集してから追加できます。
      </p>
    </div>
    <div class="form-group" style="padding:14px 16px;border:1px solid var(--line);background:var(--paper);">
      <label class="form-label">表紙の写真から自動取得</label>
      <p style="margin:0 0 8px;font-size:11px;color:var(--mute);line-height:1.6;">
        ライブラリから選ぶ、または「カメラで撮影」を押して撮影すると、自動で AI が読み取ります。
      </p>
      <input class="form-input" id="f-image" type="file" accept="image/*" style="padding:8px;" />
      <input id="f-image-camera" type="file" accept="image/*" capture="environment" hidden />
      <div id="image-preview" style="margin-top:8px;text-align:center;"></div>
      <button class="btn btn--ink btn--small btn--block" id="btn-extract-image" type="button" style="margin-top:8px;">
        <svg class="icon"><use href="#i-eye"/></svg>
        <span>カメラで撮影して読み取る</span>
      </button>
      <p style="margin:8px 0 0;font-size:10.5px;color:var(--mute);line-height:1.6;">
        書店で気になる本を見つけたら、表紙を撮影するだけで AI が書名・著者を抽出します。
      </p>
    </div>
    <div class="form-group">
      <label class="form-label">書名 <span style="color:var(--mute);font-size:11px;">必須</span></label>
      <input class="form-input" id="f-title" type="text" placeholder="例: 暇と退屈の倫理学" autocomplete="off" />
    </div>
    <div class="form-group">
      <label class="form-label">著者 <span style="color:var(--mute);font-size:11px;">必須</span></label>
      <input class="form-input" id="f-author" type="text" placeholder="例: 國分功一郎" autocomplete="off" />
    </div>
    <div class="form-group">
      <label class="form-label">ジャンル <span style="color:var(--mute);font-size:11px;">任意・複数可</span></label>
      <div id="f-genre-chips" class="genre-tabs" style="margin:6px 0 0;">${genreChips}</div>
    </div>
    <div class="form-group">
      <label class="form-label">メモ <span style="color:var(--mute);font-size:11px;">任意</span></label>
      <textarea class="form-textarea" id="f-summary" rows="3" placeholder="気になった理由、概要、参考リンクなど"></textarea>
    </div>
    <div class="form-group">
      <label class="form-label">この本との出会い <span style="color:var(--mute);font-size:11px;">任意</span></label>
      <input class="form-input" id="f-acq" type="text" placeholder="例: 書店で表紙が目に入った / 〇〇さんが薦めた / 別の本の引用で知った" autocomplete="off" />
      <p style="margin:6px 0 0;font-size:10.5px;color:var(--mute);line-height:1.6;">後でこの本を開いたとき、出会いも一緒に蘇ります</p>
    </div>
    <button class="btn btn--ink btn--block" id="btn-submit" type="button" style="margin-top:8px;">
      <svg class="icon"><use href="#i-check"/></svg>
      <span>追加する</span>
    </button>
  `;
  const screen = showScreen('wishlist-add', html);
  const selectedGenres = new Set();

  // ===== 画像 OCR セクション =====
  // ライブラリ用 input と カメラ用 input を分離。どちらに画像がセットされても同じ処理で
  // 縮小 → AI 抽出 → フォーム自動入力する。
  const fileInput = screen.querySelector('#f-image');
  const cameraInput = screen.querySelector('#f-image-camera');
  const imagePreview = screen.querySelector('#image-preview');
  const extractImageBtn = screen.querySelector('#btn-extract-image');

  async function processImageFile(file) {
    if (!file) return;
    let blob;
    try {
      blob = await downscaleImage(file, 1600);
    } catch (e) {
      console.error(e);
      toast('画像の読み込みに失敗しました');
      return;
    }
    const url = URL.createObjectURL(blob);
    imagePreview.innerHTML = `<img src="${url}" alt="" style="max-width:160px;max-height:200px;border:1px solid var(--line);" />`;
    showLoading('AI が表紙を読み取り中…');
    try {
      const base64 = await blobToBase64(blob);
      const extracted = await extractBookFromImage(base64, blob.type || 'image/jpeg');
      if (!extracted.title) {
        toast('書籍情報を抽出できませんでした');
        return;
      }
      screen.querySelector('#f-title').value = extracted.title || '';
      screen.querySelector('#f-author').value = extracted.author || '';
      screen.querySelector('#f-summary').value = extracted.summary || '';
      selectedGenres.clear();
      screen.querySelectorAll('[data-genre-chip]').forEach((c) => c.classList.remove('genre-tab--active'));
      for (const g of extracted.genre || []) {
        const chip = screen.querySelector(`[data-genre-chip="${CSS.escape(g)}"]`);
        if (chip) {
          chip.classList.add('genre-tab--active');
          selectedGenres.add(g);
        }
      }
      toast(`「${extracted.title}」を読み取りました`);
    } catch (e) {
      console.error(e);
      toast('画像の読み取りに失敗しました');
    } finally {
      hideLoading();
    }
  }

  fileInput.addEventListener('change', () => processImageFile(fileInput.files?.[0]));
  cameraInput.addEventListener('change', () => processImageFile(cameraInput.files?.[0]));
  extractImageBtn.addEventListener('click', () => {
    // カメラ起動（capture="environment"）
    cameraInput.value = '';
    cameraInput.click();
  });

  screen.querySelector('#btn-extract').addEventListener('click', async () => {
    const url = screen.querySelector('#f-url').value.trim();
    if (!url) {
      toast('URL を入力してください');
      return;
    }
    if (!/^https?:\/\//.test(url)) {
      toast('http または https の URL を入力してください');
      return;
    }
    showLoading('AI がページを読み取り中…');
    try {
      const extracted = await extractBookFromUrl(url);
      if (!extracted.title) {
        toast('書籍情報を抽出できませんでした');
        return;
      }
      screen.querySelector('#f-title').value = extracted.title || '';
      screen.querySelector('#f-author').value = extracted.author || '';
      screen.querySelector('#f-summary').value = extracted.summary || '';
      // ジャンルチップを自動チェック
      selectedGenres.clear();
      screen.querySelectorAll('[data-genre-chip]').forEach((c) => c.classList.remove('genre-tab--active'));
      for (const g of extracted.genre || []) {
        const chip = screen.querySelector(`[data-genre-chip="${CSS.escape(g)}"]`);
        if (chip) {
          chip.classList.add('genre-tab--active');
          selectedGenres.add(g);
        }
      }
      toast(`「${extracted.title}」を読み取りました`);
    } catch (e) {
      console.error(e);
      toast('URL の読み取りに失敗しました');
    } finally {
      hideLoading();
    }
  });

  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelectorAll('[data-genre-chip]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const g = chip.dataset.genreChip;
      if (selectedGenres.has(g)) {
        selectedGenres.delete(g);
        chip.classList.remove('genre-tab--active');
      } else {
        selectedGenres.add(g);
        chip.classList.add('genre-tab--active');
      }
    });
  });
  screen.querySelector('#btn-submit').addEventListener('click', async () => {
    const title = screen.querySelector('#f-title').value.trim();
    const author = screen.querySelector('#f-author').value.trim();
    if (!title || !author) {
      toast('書名と著者を入力してください');
      return;
    }
    const genres = Array.from(selectedGenres);
    const summary = screen.querySelector('#f-summary').value.trim();
    const id = wishlistId(title, author);
    const acquisition = screen.querySelector('#f-acq').value.trim();
    await addToWishlist({
      id,
      title,
      author,
      genre: genres.length ? genres.join(' · ') : '',
      summary,
      learnings: [],
      reason: '',
      acquisition,
      manual: true,
    });
    toast('購入予定リストに追加しました');
    const items = await getAllWishlist();
    renderList(items);
  });
}

function bindItemHandlers(screen) {
  screen.querySelectorAll('[data-wishlist-card]').forEach((card) => {
    const id = card.dataset.wishlistCard;
    card.querySelector('[data-toggle-details]').addEventListener('click', () => {
      const det = card.querySelector('[data-details]');
      det.hidden = !det.hidden;
      card.querySelector('[data-chevron]').textContent = det.hidden ? '＋' : '−';
    });
    card.querySelector('[data-remove]').addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!confirm('リストから削除しますか？')) return;
      await removeFromWishlist(id);
      const fresh = await getAllWishlist();
      renderList(fresh);
      toast('削除しました');
    });
    const purchasedBtn = card.querySelector('[data-purchased]');
    if (purchasedBtn) {
      purchasedBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const data = JSON.parse(card.dataset.itemJson || '{}');
        await markAsPurchased(id, data);
        const fresh = await getAllWishlist();
        renderList(fresh);
      });
    }
  });
}

async function markAsPurchased(id, item) {
  const genre = item.genre
    ? String(item.genre).split(/\s*·\s*|\s*,\s*/).filter(Boolean)
    : [];
  await addLocalBook({
    name: item.title,
    author: item.author,
    genre,
    summary: item.summary,
    learnings: item.learnings || [],
    reason: item.reason,
    fromWishlistId: id,
    acquisition: item.acquisition,
  });
  await removeFromWishlist(id);
  toast('積読リストに移動しました');
}

function renderItem(it) {
  const learnings = (it.learnings || [])
    .map((l) => `<li>${escapeHtml(l)}</li>`)
    .join('');
  const dateStr = it.addedAt
    ? new Date(it.addedAt).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })
    : '';
  // HTML 属性に JSON を埋め込むため、" を &quot; に明示的にエスケープ
  // （escapeHtml は textContent 経由で " をエスケープしないため）
  const itemJson = JSON.stringify({
    title: it.title,
    author: it.author,
    genre: it.genre,
    summary: it.summary,
    learnings: it.learnings || [],
    reason: it.reason,
  })
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const manualBadge = it.manual
    ? '<span class="book-meta__tag" style="margin-left:6px;">手動追加</span>'
    : '';
  return `
    <article class="result-card" data-wishlist-card="${escapeHtml(it.id)}" data-item-json="${itemJson}">
      <div style="display:flex;align-items:flex-start;gap:12px;">
        <div style="flex:1;min-width:0;">
          <h3 class="result-card__title">${escapeHtml(it.title)}${manualBadge}</h3>
          <p class="result-card__meta">${escapeHtml(it.author || '')}${it.genre ? ' · ' + escapeHtml(it.genre) : ''}</p>
          <p class="result-card__meta" style="margin-top:4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.08em;color:var(--mute);">追加: ${dateStr}</p>
        </div>
        <button data-toggle-details class="btn btn--icon" type="button" aria-label="詳細"><span data-chevron style="font-family:var(--font-serif);font-size:18px;line-height:1;">＋</span></button>
      </div>
      <div data-details hidden style="margin-top:12px;padding-top:12px;border-top:1px solid var(--line);">
        ${it.summary ? `<p class="result-card__body"><strong>概要:</strong> ${escapeHtml(it.summary)}</p>` : ''}
        ${learnings ? `<div style="margin-top:10px;"><p class="result-card__body" style="margin-bottom:4px;"><strong>学べること:</strong></p><ul style="margin:0;padding-left:18px;font-size:13px;color:var(--dim);line-height:1.7;">${learnings}</ul></div>` : ''}
        ${it.reason ? `<p class="result-card__body" style="margin-top:10px;"><strong>推薦理由:</strong> ${escapeHtml(it.reason)}</p>` : ''}
        ${it.acquisition ? `<p class="result-card__body" style="margin-top:10px;"><strong>出会い:</strong> ${escapeHtml(it.acquisition)}</p>` : ''}
        <div class="result-card__chips" style="margin-top:14px;">
          <a class="chip" href="${escapeHtml(amazonSearchUrl(it.title, it.author))}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">Amazon</a>
          <a class="chip" href="${escapeHtml(koshoSearchUrl(it.title, it.author))}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">日本の古本屋</a>
        </div>
        <button data-purchased class="btn btn--small btn--block btn--ink" type="button" style="margin-top:12px;">
          <svg class="icon"><use href="#i-check"/></svg>
          <span>購入済み → 積読リストへ</span>
        </button>
        <button data-remove class="btn btn--small btn--block btn--ghost" type="button" style="margin-top:8px;color:var(--mute);">
          <svg class="icon"><use href="#i-close"/></svg>
          <span>リストから削除</span>
        </button>
      </div>
    </article>
  `;
}
